import { ApplicationConfig, APP_ID, provideZoneChangeDetection, PLATFORM_ID, provideAppInitializer, inject, ErrorHandler } from '@angular/core';
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { LAZYLOAD_IMAGE_HOOKS } from 'ng-lazyload-image';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ProductRouteReuseStrategy } from './modules/shared/guards/product-route-reuse.strategy';
import { DynamicScript } from './modules/shared/services/dynamic-script.service';
import { StoreRegionService } from './modules/shared/services/store-region.service';
import { httpInterceptor } from './modules/shared/services/interceptor/http-interceptor.service';
import { errorHandlerInterceptor } from './modules/shared/services/interceptor/error-handler.service';
import { httpCacheInterceptor } from './modules/shared/services/http-cache/http-cache.service';
import { CanonicalUrlService } from './modules/shared/services/canonical-url.service';
import { SeoScriptsService } from './modules/shared/services/seo-scripts.service';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { ApiUrls } from './modules/shared/enum/routes';
import { utmTransferInitializer } from './modules/shared/services/utm-transfer.initializer';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'YYYY.MM.DD'
  },
  display: {
    dateInput: 'yyyy.MMM.DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

// export function initApp(
//   storeRegionService: StoreRegionService,
//   router: Router,
//   platformId: Object
// ) {
//   return () => {
//     // Skip navigation during SSR to prevent timeout
//     if (isPlatformServer(platformId)) {
//       return Promise.resolve();
//     }

//     const selectedRegion = storeRegionService.selectedRegion;
//     if (selectedRegion && typeof window !== 'undefined') {
//       const currentPath = window.location.pathname;
//       const currentSearch = window.location.search;
//       if (!currentSearch && (currentPath === '/' || currentPath === '')) {
//         if (selectedRegion.has_homepage) {
//           router.navigate(['/', selectedRegion.code]);
//         } else {
//           router.navigate(['/'], {
//             queryParams: { region: selectedRegion.code }
//           });
//         }
//       }
//     }
//     return Promise.resolve();
//   };
// }

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withComponentInputBinding()
    ),
    // { provide: RouteReuseStrategy, useClass: ProductRouteReuseStrategy },
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        // Homepage is region- and auth-specific; replaying the SSR GET on the client
        // makes club-member refresh look successful in console but skips the network.
        filter: (req) => !req.url.includes(ApiUrls.home)
      }),
      withEventReplay()
    ),
    { provide: APP_ID, useValue: 'serverApp' },
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => {
      const platformId = inject(PLATFORM_ID);
      // Only set persistence in browser, not during SSR
      // Use Angular's platform check for better SSR safety
      if (isPlatformBrowser(platformId) && typeof window !== 'undefined' && typeof document !== 'undefined') {
        try {
          const auth = getAuth(getApp());
          setPersistence(auth, browserLocalPersistence);
          return auth;
        } catch (error) {
          // Ignore errors during SSR or if persistence fails
        }
      }
    }),
    DynamicScript,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    // { provide: LAZYLOAD_IMAGE_HOOKS, useClass: LazyLoadService },
    // {
    //   provide: ErrorHandler,
    //   useClass: ErrorLogger
    // },
    // {
    //   provide: Sentry.TraceService,
    //   deps: [Router]
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => { },
    //   deps: [Sentry.TraceService],
    //   multi: true
    // },
    provideAppInitializer(() => {
      const router = inject(Router);
      const storeRegionService = inject(StoreRegionService);
      const platformId = inject(PLATFORM_ID);
      // Force eager instantiation so navigation listeners are attached at app startup.
      inject(CanonicalUrlService);
      inject(SeoScriptsService);
      if (isPlatformServer(platformId)) {
        return Promise.resolve();
      }
  
      const selectedRegion = storeRegionService.selectedRegion;
      if (selectedRegion && isPlatformBrowser(platformId) && typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        if (!currentSearch && (currentPath === '/' || currentPath === '')) {
          if (selectedRegion.has_homepage) {
            router.navigate(['/', selectedRegion.code]);
          } else {
            router.navigate(['/'], {
              queryParams: { region: selectedRegion.code }
            });
          }
        }
      }
      return Promise.resolve();
    }),
    provideAppInitializer(utmTransferInitializer),
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initApp,
    //   deps: [StoreRegionService, Router, PLATFORM_ID],
    //   multi: true
    // },
    CanonicalUrlService,
    SeoScriptsService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en',
      lang: 'en'
    }),
    provideHttpClient(
      // Error handler first so token refresh retries run through httpInterceptor + cache again.
      withInterceptors([errorHandlerInterceptor, httpInterceptor, httpCacheInterceptor]),
      withFetch()
    ),
  ]
};

